/**
 * Core: Quicklink
 * -----------------------------------------------------------------------------
 * Setup quicklink URL prefetching.
 * - https://github.com/GoogleChromeLabs/quicklink
 *
 */
import { listen } from 'quicklink'

export default () => {

  /**
   * Initialise Quicklink.
   */
  function init() {
    const publishedTheme = cnvs.theme.role
      ? cnvs.theme.role === 'main'
      : false

    /**
     * Do not run quicklink on unpublished themes.
     * - Prefetched pages can lose the current theme preview.
     * - If theme name or role not found then default to not running quicklink.
     * - Also disable in Storybook and theme editor.
     */
    if (
      !cnvs.settings.pagePrefetching.enable ||
      !publishedTheme ||
      cnvs.storybook ||
      Shopify.designMode
    ) {
      return
    }

    /**
     * Build array of URL pathnames to ignore from theme settings.
     */
    const ignoreUrls = cnvs.settings.pagePrefetching.ignoreUrls
      .split('\n')
      .map((url) => url.trim().toLowerCase())

    /**
     * Configure listen.
     * - https://github.com/GoogleChromeLabs/quicklink#quicklinklistenoptions
     * - Prefetch URL when it's 100% in viewport for a minimum of 1 second.
     * - Limited to four URL prefetches at a time.
     */
    listen({
      delay: 1000,
      ignores: (testUrl, element) => getIgnoreUrl(testUrl, ignoreUrls, element),
      threshold: 1,
      throttle: 4,
    })
  }

  /**
   * Determine if URL should be ignored based on theme settings.
   * @param {String} testUrl - URL that requires testing.
   * @param {Array} ignoreUrls - Array of URL pathnames to ignore.
   * @param {HTMLElement} element - Element which links to URL.
   * @returns {Boolean}
   */
  function getIgnoreUrl(testUrl, ignoreUrls) {
    const formattedTestUrl = new URL(testUrl)
    const testPathname = formattedTestUrl.pathname

    if (ignoreUrls.some((ignoreUrl) => ignoreUrl === testPathname)) {
      return true
    }

    /**
     * Go through each part of each ignore URL.
     * - If '#' then check if test part is a number, if so then it's a match.
     * - If '*' then it matches everything and every proceeding part matches
     *   as well.
     */
    const testParts = testPathname.split('/')

    return ignoreUrls.some((ignoreUrl) => {
      const ignoreParts = ignoreUrl.split('/')
      let afterWildcard = false

      const complexMatch = testParts.every((testPart, index) => {
        const ignorePart = ignoreParts[index]

        if (
          afterWildcard ||
          (ignorePart === '#' && testPart !== '' && !isNaN(testPart)) ||
          ignorePart === '*'
        ) {
          if (ignorePart === '*') {
            afterWildcard = true
          }

          return true
        }

        return ignorePart === testPart
      })

      return complexMatch
    })
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
